import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import './Tab.scss';

export function TabBadge({
    count,
    ariaLabel,
    ariaLabelKey,
    className = '',
    dot = false,
}) {
    const { t } = useTranslation();
    const numericCount = typeof count === 'number' ? count : null;
    const hasVisibleCount = numericCount != null && numericCount > 0;

    if (dot && !hasVisibleCount) {
        return null;
    }

    if (!dot && numericCount == null) {
        return null;
    }
    const badgeLabel = dot ? null : (numericCount > 99 ? '99+' : numericCount);
    const ariaCount = numericCount == null ? 0 : (dot ? numericCount : Math.min(numericCount, 99));
    const resolvedAriaLabel = ariaLabel || (
        ariaLabelKey
            ? t(ariaLabelKey, { count: ariaCount })
            : null
    );

    return (
        <span
            className={['tab__badge', dot && 'tab__badge--dot', className].filter(Boolean).join(' ')}
            aria-label={resolvedAriaLabel || undefined}
            aria-hidden={resolvedAriaLabel ? undefined : dot}
        >
            {badgeLabel}
        </span>
    );
}

TabBadge.propTypes = {
    count: PropTypes.number,
    ariaLabel: PropTypes.string,
    ariaLabelKey: PropTypes.string,
    className: PropTypes.string,
    dot: PropTypes.bool,
};

export const Tab = forwardRef(function Tab(
    {
        id,
        className,
        onClick,
        onKeyDown,
        onFocus,
        onMouseEnter,
        label,
        badge,
        badgeAriaLabel,
        badgeAriaLabelKey,
        icon: Icon,
        isActive,
        orientation,
    },
    ref,
) {
    const badgeValue = typeof badge === 'number' ? badge : null;
    const resolvedBadgeAriaLabelKey = badgeAriaLabel
        ? null
        : (badgeAriaLabelKey || 'navigation.unreadBadge');

    return (
        <button
            id={id}
            ref={ref}
            type="button"
            className={className}
            onClick={onClick}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            onMouseEnter={onMouseEnter}
            data-active={isActive}
            data-orientation={orientation}
            data-tab-id={id}
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
        >
            {Icon && (
                <span className="tab__icon" aria-hidden="true">
                    <Icon />
                </span>
            )}
            <span className="tab__label">{label}</span>
            <TabBadge
                count={badgeValue}
                ariaLabel={badgeAriaLabel}
                ariaLabelKey={resolvedBadgeAriaLabelKey}
            />
        </button>
    );
});

Tab.propTypes = {
    id: PropTypes.string.isRequired,
    className: PropTypes.string,
    onClick: PropTypes.func,
    onKeyDown: PropTypes.func,
    onFocus: PropTypes.func,
    onMouseEnter: PropTypes.func,
    label: PropTypes.node,
    badge: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    badgeAriaLabel: PropTypes.string,
    badgeAriaLabelKey: PropTypes.string,
    icon: PropTypes.elementType,
    isActive: PropTypes.bool,
    orientation: PropTypes.oneOf(['vertical', 'horizontal']),
};

Tab.defaultProps = {
    className: '',
    onClick: undefined,
    onKeyDown: undefined,
    onFocus: undefined,
    onMouseEnter: undefined,
    label: null,
    badge: undefined,
    badgeAriaLabel: undefined,
    badgeAriaLabelKey: undefined,
    icon: undefined,
    isActive: false,
    orientation: 'vertical',
};
